// src/api/shiftsApi.ts

import type { ShiftDto } from "../components/shifts/ShiftCard";
import type { ShiftParticipantsResponse } from "../components/shifts/ParticipantsModal";

export type EventItem = { eventID: number; eventName: string };
export type FunctionItem = { functionId: number; functionName: string; eventID: number; functionCategoryId: number; categoryName: string; description: string};

export type ShiftUpsertDto = {
    functionId: number;
    shiftsName: string;
    startTime: string; // ISO
    endTime: string;   // ISO
    maxParticipants: number;
    waitlist: boolean;
    ticketID?: number | null;
};

// ---------- helpers ----------
async function readText(r: Response): Promise<string> {
    try {
        return await r.text();
    } catch {
        return "";
    }
}

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
    const r = await fetch(url, init);
    const text = await readText(r);

    if (!r.ok) {
        throw new Error(`HTTP ${r.status} ${r.statusText}. Body: ${text}`);
    }

    if (!text) return [] as unknown as T;

    try {
        return JSON.parse(text) as T;
    } catch {
        // hvis server returnerer plain text men du forventer json
        return text as unknown as T;
    }
}

function qs(params: Record<string, string | number | undefined | null>) {
    const p = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v === undefined || v === null || v === "") return;
        p.set(k, String(v));
    });
    const s = p.toString();
    return s ? `?${s}` : "";
}

// ---------- API ----------
export async function getEvents(apiUrl: string): Promise<EventItem[]> {
    const data = await fetchJson<EventItem[]>(`${apiUrl}/api/events`);
    return Array.isArray(data) ? data : [];
}

export async function getFunctions(apiUrl: string, eventId: number): Promise<FunctionItem[]> {
    const data = await fetchJson<FunctionItem[]>(`${apiUrl}/api/events/${eventId}/functions`);
    return Array.isArray(data) ? data : [];
}

export async function getShiftsWithCounts(
    apiUrl: string,
    eventId: number,
    opts?: { functionId?: number; date?: string } // date = yyyy-mm-dd
): Promise<ShiftDto[]> {
    const query = qs({
        functionId: opts?.functionId,
        date: opts?.date,
    });

    const url = `${apiUrl}/api/events/${eventId}/shifts/with-counts${query}`;
    const data = await fetchJson<ShiftDto[]>(url);
    return Array.isArray(data) ? data : [];
}

export async function getShiftParticipants(
    apiUrl: string,
    shiftId: number
): Promise<ShiftParticipantsResponse> {
    return await fetchJson<ShiftParticipantsResponse>(
        `${apiUrl}/api/shifts/${shiftId}/participants`
    );
}

export async function createShift(apiUrl: string, payload: ShiftUpsertDto): Promise<void> {
    await fetchJson<void>(`${apiUrl}/api/shifts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
}

export async function updateShift(
    apiUrl: string,
    shiftId: number,
    payload: ShiftUpsertDto
): Promise<void> {
    await fetchJson<void>(`${apiUrl}/api/shifts/${shiftId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
}

export async function deleteShift(apiUrl: string, shiftId: number): Promise<void> {
    await fetchJson<void>(`${apiUrl}/api/shifts/${shiftId}`, {
        method: "DELETE",
    });
}

export async function getAllowedEventIds(apiUrl: string, userId: number): Promise<number[]> {
    const res = await fetch(`${apiUrl}/api/users/allowed-events?userId=${userId}`);
    if (!res.ok) throw new Error("Failed to load allowed events");
    return await res.json(); // fx [1,2,3]
}
